// Modules
import { ErrorResponse } from 'apollo-link-error'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { UseFormSetError } from 'react-hook-form'

// Components
import { Errors } from 'redesign/constants'
import { useLoginContext } from 'context'
import { OutputErrorCode } from 'constants/index'
import { useLoginService } from 'services/useLoginService'
import { LoginType, SignInCIPCredentials } from 'typings/auth'
import { convertToNumber, isEmpty, singleSPA } from 'utils'

import { useSnackbar } from '../useSnackbar'
import { ROUTES_PATH } from 'constants/routesPath'
import { useTrackEventError } from '../useTrackEventError'
import { useCurrentLaboratory } from '../useCurrentLaboratory'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'

export type FormInputs = {
    cipAndFap: string
    password: string
    birthDate: string
}

interface handleLoginWithCipAndFapProps {
    setIsSubmitting: (value: boolean) => void
    setError: UseFormSetError<FormInputs>
    cipAndFap: string
    password: string
    birthDate: string
}

export function useSelfServiceCip() {
    const { authenticateCIP } = useLoginService()
    const { isFeatureEnabled } = useRemoteConfig()
    const { operations } = useLoginContext()
    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')
    const { handleTrackEventError } = useTrackEventError()
    const { openErrorSnackbarRetry } = useSnackbar()
    const { executeRecaptcha = () => '' } = useGoogleReCaptcha()
    const { currentLaboratory } = useCurrentLaboratory()

    const authenticateCIPAndFap = async (credentials: SignInCIPCredentials): Promise<void> => {
        const data = await authenticateCIP(credentials)

        if (data) {
            operations.addPayloadTokenLS({ token: data.accessToken, refreshToken: '', limitedFlow: true, selfLogin: true })
            operations.setIdentifier(LoginType.empty)
        }
    }

    async function handleLoginWithCipAndFap({
        setIsSubmitting,
        birthDate,
        cipAndFap,
        password,
        setError,
    }: handleLoginWithCipAndFapProps) {
        try {
            setIsSubmitting(true)
            const recaptchaToken = await executeRecaptcha('login')

            const payload: SignInCIPCredentials = {
                cip: convertToNumber(cipAndFap),
                password,
                dateOfBirth: birthDate,
                lab: currentLaboratory.name,
                recaptchaToken: recaptchaToken || '',
                isInvalid: false,
            }

            await authenticateCIPAndFap(payload)

            if (newRedirectFlow) {
                return singleSPA.historyPush(`${ROUTES_PATH.FORWARDING_APP}?redirectTo=${ROUTES_PATH.EXAMS_SELF_SERVICE}`)
            }
            return singleSPA.historyPush(ROUTES_PATH.EXAMS_SELF_SERVICE)
        } catch (err) {
            const e: ErrorResponse = err
            if (err.graphQLErrors && !isEmpty(err.graphQLErrors)) {
                const codeError = err.graphQLErrors[0].extensions?.code

                handleTrackEventError({
                    codeError: codeError || e.networkError?.name || e.response?.toString() || '',
                    message: `${err.graphQLErrors[0]?.message || e.networkError?.message} - AuthenticateCIP`,
                    accessType: 'CIP',
                    access: 'autoatendimento',
                })

                if ([OutputErrorCode.CIPUNAUTHENTICATED, OutputErrorCode.RECAPTCHATOKENINVALID].includes(codeError)) {
                    setError('cipAndFap', {}, { shouldFocus: false })
                    setError('password', {}, { shouldFocus: false })
                    setError('birthDate', { message: Errors.selfService.CIPWITHOUTACCOUNT }, { shouldFocus: false })
                    return
                }

                openErrorSnackbarRetry({
                    message: Errors.selfService.DEFAULT,
                    onClick: () => handleLoginWithCipAndFap({ birthDate, cipAndFap, password, setError, setIsSubmitting }),
                })
            }
            return
        } finally {
            setIsSubmitting(false)
        }
    }

    return {
        handleLoginWithCipAndFap,
    }
}
